// Story 6.9 (ADO-1163, AC4) — Modal / Dialog (NEW primitive; focus-trapped).
// Figma Edit User / Assign role / Deactivate / Unblock dialogs (572:3553).
//
// a11y (NFR24–26): role="dialog" + aria-modal, labelled by its title. On open it
// moves focus into the dialog and traps Tab within it; Escape and the close
// button both dismiss; focus is restored to the previously-focused element on
// close. The backdrop click also dismisses.
//
// `animated` (ADO-911, the PDP image zoom) fades the dialog in and out. It is opt-in so
// every other dialog keeps mounting and unmounting at once.

import { useCallback, useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useExitTransition } from '../hooks/useExitTransition';
import { useSheetDrag } from '../hooks/useSheetDrag';
import { MOTION_MS } from '../motion';
import styles from './Modal.module.css';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional sub-title line under the title. */
  description?: ReactNode;
  children: ReactNode;
  /** Footer actions (e.g. Cancel / Save Changes buttons). */
  footer?: ReactNode;
  /** Story 7.12 Iteration 2 (ADO-911) — dialog width. `default` is the 420px
   *  form/confirm dialog every 6.10/6.11 surface uses; `wide` is for content
   *  dialogs that lay cards out side by side (the Linked Product upsell modal,
   *  Figma 1269:7109), which are unreadable squeezed into 420px. `fullscreen`
   *  (Story 7.18 redesign) is the edge-to-edge camera scanner: the dialog fills
   *  the viewport with no card chrome so the caller owns the whole canvas, while
   *  the focus-trap / Escape / scroll-lock / portal contract below stays intact. */
  size?: 'default' | 'wide' | 'fullscreen';
  /** Suppress the default header (title + close button) so the caller can render
   *  its own top bar edge-to-edge (the fullscreen scanner's cart chip + close).
   *  The `title` is still emitted as a visually-hidden `<h2>` so `aria-labelledby`
   *  keeps naming the dialog for assistive tech (AC12 / NFR24-26). */
  hideChrome?: boolean;
  /** Story 8.1 (ADO-2010): where the dialog sits. `center` is the card every other
   *  dialog uses. `bottom` is a mobile bottom sheet (Figma 5243:9014): full width,
   *  rounded top corners, a grab handle, capped below the header; a downward drag on its
   *  header row dismisses it like Close (ADO-1779, `useSheetDrag`). `end`
   *  is a tablet side tray (Figma 5243:9593): full height, anchored to the right
   *  edge, 380px wide. Both keep this primitive's dialog contract (focus trap,
   *  Escape, scroll lock, portal, scrim press) and ignore `size`. */
  placement?: 'center' | 'bottom' | 'end';
  /** ADO-1779: the inset of a `bottom` sheet's header, body and footer. `default` is
   *  the /choose sheet's 16px; `compact` is the 12px the Previous Orders sheets draw
   *  (Figma 5415:22157). Ignored by the other placements. */
  sheetGutter?: 'default' | 'compact';
  /** Story 8.6 (ADO-2015): a decorative icon before the title of the centred dialog (the
   *  "Order sent" check, Figma 5955:30315). Sheets do not draw it; a sheet that needs one puts
   *  it in its body (Figma 5955:30427). */
  icon?: ReactNode;
  /** ADO-911: fade the overlay in on open and out on close (`--motion-fast` in,
   *  `--motion-exit-fast` out). While the fade-out runs the dialog stays mounted but inert,
   *  so it takes no input and cannot call `onClose` again. Under reduced motion it
   *  unmounts at once. Off by default, which mounts and unmounts with `open`. */
  animated?: boolean;
}

/** Story 8.3 (ADO-2012), card 10: the close of every open dialog, so a screen that must show
 *  something of its own over the page (the cart side panel) can close them first, as the
 *  person would, rather than stack a second dialog. */
const openModalCloses = new Set<() => void>();

/** Close every open `Modal` through its own `onClose` (Story 8.3, ADO-2012, card 10). */
export function closeOpenModals(): void {
  for (const close of [...openModalCloses]) close();
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'default',
  hideChrome = false,
  placement = 'center',
  sheetGutter = 'default',
  icon,
  animated = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Story 8.3 (ADO-2012), card 10: registered while open, through the latest `onClose`.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!open) return;
    const close = () => onCloseRef.current();
    openModalCloses.add(close);
    return () => {
      openModalCloses.delete(close);
    };
  }, [open]);
  const descId = useId();

  // Only an animated dialog outlives `open`. A plain one never feeds the hook a change, so
  // `present` is `open` and its mount, unmount and effects are what they always were.
  const exit = useExitTransition(animated && open, MOTION_MS.exitFast);
  const present = animated ? exit.present : open;
  const closing = animated && exit.closing;

  // Focus trap + initial focus + focus-restore-on-close (shared with FiltersDrawer).
  const trapTab = useFocusTrap(open, dialogRef);
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // A fading-out dialog is already closed: `inert` keeps keys away from it, and this
      // holds where `inert` is not supported.
      if (closing) return;
      // Code review follow-up (Story 7.16, ADO-1373): a NESTED dialog — the
      // non-modal `Popover` this modal's `ProductCard` children can open — can
      // legitimately own focus without being a DOM descendant of `dialogRef`, since
      // it portals elsewhere in the DOM. React still dispatches this keydown here
      // because bubbling follows the FIBER tree (component ancestry), not DOM
      // position, so without this check a keypress meant for the nested dialog
      // would ALSO fire this modal's own handling: Escape would close the whole
      // modal instead of just the popover, and the focus trap below would yank
      // focus out of the popover and back into the modal. `closest` walks the REAL
      // DOM, so it correctly finds the popover's own `role="dialog"` rather than
      // this modal's — let the nested dialog's own contract (or plain DOM order,
      // for the popover, which deliberately does not trap Tab) handle the key.
      // `?? null` normalises a null/absent `activeElement` to the SAME "nothing
      // to defer to" value `.closest()` itself returns when there is no dialog
      // ancestor — without it, optional chaining short-circuits to `undefined`,
      // which the check below would wrongly treat as "a nested dialog owns
      // focus" and bail out of even normal (non-nested) Escape/Tab handling.
      const activeDialog =
        (document.activeElement as HTMLElement | null)?.closest('[role="dialog"]') ?? null;
      if (activeDialog !== null && activeDialog !== dialogRef.current) return;

      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      trapTab(e);
    },
    [closing, onClose, trapTab],
  );

  // Lock body scroll while open so the page behind can't scroll under the overlay
  // (wheel / PgDn); restore the previous inline value on close. This is the base
  // primitive for every 6.10/6.11 dialog, so the lock is part of its contract. The
  // lock is counted, so a Modal opened from another overlay (the Clear confirm in
  // the cart side panel) releases correctly in either order. (Focus capture/restore
  // lives in useFocusTrap.)
  // Keyed on `present`, not `open`: the lock holds while an animated dialog fades out, so
  // the page's scrollbar does not come back and shift the layout under the fading scrim.
  // Focus, above, still keys on `open` and goes back to the trigger as the fade starts.
  useBodyScrollLock(present);

  const sheetDrag = useSheetDrag(open && placement === 'bottom', dialogRef, onClose);

  if (!present) return null;

  const sheet = placement !== 'center';
  const overlayClass = sheet
    ? `${styles.overlay} ${placement === 'bottom' ? styles.overlayBottom : styles.overlayEnd}`
    : `${styles.overlay} ${size === 'fullscreen' ? styles.overlayFullscreen : ''}`.trim();
  const dialogClass = sheet
    ? `${styles.dialog} ${styles.sheet} ${placement === 'bottom' ? styles.sheetBottom : styles.sheetEnd}`
    : `${styles.dialog} ${size === 'wide' ? styles.dialogWide : ''} ${
        size === 'fullscreen' ? styles.dialogFullscreen : ''
      }`.trim();
  const bodyClass = sheet
    ? styles.sheetBody
    : size === 'fullscreen'
      ? styles.bodyFullscreen
      : styles.body;

  // Portal to <body> so the fixed overlay isn't trapped/clipped by an ancestor's
  // stacking context — any ancestor with transform/filter/backdrop-filter would
  // otherwise become the containing block for `position: fixed`. This is the base
  // primitive every 6.10/6.11 dialog sits on, so the DOM placement is a contract.
  return createPortal(
    // Backdrop click-to-dismiss is a POINTER-ONLY convenience: the dialog is fully
    // keyboard-dismissible via Escape (onKeyDown) and the Close button, per the
    // WAI-ARIA dialog pattern — so the overlay handler needs no keyboard listener
    // or role. jsx-a11y can't infer that, hence the scoped disable.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={overlayClass}
      data-state={animated ? (closing ? 'closing' : 'open') : undefined}
      // A closing dialog is on its way out: `inert` keeps it from taking focus, keys or
      // presses (no second `onClose`) and drops it from the accessibility tree.
      inert={closing}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !closing) onClose();
      }}
      onAnimationEnd={(e) => {
        // Only the overlay's own fade ends the exit, not the dialog's scale bubbling up.
        if (closing && e.target === e.currentTarget) exit.finish();
      }}
    >
      {/* The onKeyDown implements the dialog's Escape-to-close + Tab focus-trap
          (WAI-ARIA dialog pattern). A keydown handler on the role="dialog"
          container is the intended structure, not a noninteractive-element misuse. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        data-placement={placement}
        data-sheet-gutter={placement === 'bottom' ? sheetGutter : undefined}
        data-has-footer={footer ? 'true' : undefined}
        className={dialogClass}
        onKeyDown={onKeyDown}
      >
        {hideChrome ? (
          // No visible chrome — the caller renders its own top bar. Keep the title
          // as a visually-hidden label so aria-labelledby still names the dialog.
          <h2 id={titleId} className={styles.srOnly}>
            {title}
          </h2>
        ) : (
          <div className={sheet ? styles.sheetHeader : styles.header} {...sheetDrag}>
            {placement === 'bottom' ? (
              <span className={styles.grabHandle} data-part="grab-handle" aria-hidden="true" />
            ) : null}
            <div className={styles.titleGroup}>
              {icon && !sheet ? (
                <div className={styles.titleRow}>
                  <span className={styles.titleIcon} aria-hidden="true">
                    {icon}
                  </span>
                  <h2 id={titleId} className={styles.title}>
                    {title}
                  </h2>
                </div>
              ) : (
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
              )}
              {description ? (
                <p id={descId} className={styles.description}>
                  {description}
                </p>
              ) : null}
            </div>
            <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
              <Icon name="x" size={20} />
            </button>
          </div>
        )}
        <div className={bodyClass}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
